import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { useSelector } from 'react-redux'

import styled, { useTheme } from 'styled-components/native'

import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { ChatSideBarSelection } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/chatRightSideBar/ChatRightSideBar'
import { ProviderChatHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/providerChatHeader/ProviderChatHeader'
import { ChatHandle } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/chat/Chat'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { CompactMessengerHeader, CompactMessengerHeaderProps } from './CompactMessengerHeader'
import MessengerEmptyView from './MessengerEmptyView'
import { ProviderPairStatusBadge } from './ProviderPairStatusBadge'
import { getAuthUser } from '../../../data/auth/authSelectors'
import { Template } from '../../documents/data/templatesApi'
import BaseChat, { BaseChatHandle, ChatEntryPoint, InsertMessageOptions } from '../BaseChat'
import { getProviderPairPresentation, ProviderPairCounterpartStatus } from '../data/providerPairPresentation'
import { ProviderPairAccess } from '../data/providerPairUtils'
import useMarkMessagesUnread from '../hooks/useMarkMessagesUnread'

type MessengerProps = {
  compactHeader?: Omit<CompactMessengerHeaderProps, 'clientName' | 'onExpand' | 'headerNote'> & {
    onExpand?: (message: string) => void
  }
  selectedEpisodeId?: string
  clientName?: string
  title?: React.ReactNode
  isClientTitle: boolean
  onOpenClientProfile: () => void
  isLoading: boolean
  isVerifyingSelectedThread: boolean
  showEmptyView: boolean
  onOpenDetailsPanel: () => void
  isDetailsPanelOpen: boolean
  isComposing: boolean
  clients: { id: string; name: string }[]
  onMessageSent: () => void
  onClientSelected: (clientId: string | null) => void
  onOpenNewWindow?: () => void
  onClose?: () => void
  // Receives the unsent composer text so Message center can continue it.
  onOpenMessages?: (message: string) => void
  entryPoint?: ChatEntryPoint
  messageToInsert: { message: string; opts?: InsertMessageOptions } | null
  onMessageInserted: () => void
  onReadOnlyInsertAttempt: () => void
  shouldInsertDBL: boolean
  onDBLInserted: () => void
  shouldInsertEssentialsLink: boolean
  onEssentialsLinkInserted: () => void
  currentTemplateId?: string
  tabs: { id: string; label: React.ReactNode; badgeCount?: number; iconWithTooltip?: React.ReactNode }[]
  selectedTabId: string
  onTabSelected: (tabId: string) => void
  ensureConversation: () => Promise<string | null>
  canSendMessages: boolean
  showProviderPairResponseTimeNotice: boolean
  providerPairAccess?: ProviderPairAccess
  providerPairStatus?: ProviderPairCounterpartStatus
  showInputActions: boolean
  disableDrafts: boolean
  hasRecipientMessages: boolean
  recipientName?: string
  recipientId?: string
  patientFullName?: string
  guardianName?: string
  onOpenDocumentLibrary?: () => void
  onOpenSideBarItem?: (selection: ChatSideBarSelection) => void
  documentToAttach: Template | null
  onDocumentAttached: () => void
}

const PANEL_WIDTH = 320

const Container = styled(Animated.View)({
  flexDirection: 'column',
  flex: 1,
})

const VerificationContainer = styled.View({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
})

const ProviderPairResponseTimeNotice = () => {
  const { colors, spacing } = useTheme()

  return (
    <InlineInfoBanner
      testId='ProviderPairResponseTimeNotice'
      backgroundColor={colors.backgroundElement}
      textColor={colors.textSecondary}
      iconColor={colors.iconDefault}
      marginBottom={spacing['16px']}
      iconSize={16}
      text={
        <FormattedMessage
          defaultMessage='Response times from Lyra contractors may be longer.'
          description='Notice shown above the composer in provider-to-provider messaging threads'
        />
      }
    />
  )
}

const MessengerChat: React.FC<MessengerProps> = ({
  compactHeader,
  selectedEpisodeId,
  clientName,
  title,
  isClientTitle,
  onOpenClientProfile,
  isLoading,
  isVerifyingSelectedThread,
  showEmptyView,
  onOpenDetailsPanel,
  isDetailsPanelOpen,
  isComposing,
  clients,
  onMessageSent,
  onClientSelected,
  onOpenNewWindow,
  onClose,
  onOpenMessages,
  entryPoint,
  messageToInsert,
  onMessageInserted,
  onReadOnlyInsertAttempt,
  shouldInsertDBL,
  onDBLInserted,
  shouldInsertEssentialsLink,
  onEssentialsLinkInserted,
  currentTemplateId,
  tabs,
  selectedTabId,
  onTabSelected,
  ensureConversation,
  canSendMessages,
  showProviderPairResponseTimeNotice,
  providerPairAccess,
  providerPairStatus,
  showInputActions,
  disableDrafts,
  hasRecipientMessages,
  recipientName,
  recipientId,
  patientFullName,
  guardianName,
  onOpenDocumentLibrary,
  onOpenSideBarItem,
  documentToAttach,
  onDocumentAttached,
}) => {
  const showEmptyComposeView = isComposing && !clientName
  const shouldPanelClose = showEmptyView || showEmptyComposeView || !isDetailsPanelOpen
  const prevPanelState = usePrevious(isDetailsPanelOpen)
  const contentMarginRight = useSharedValue(shouldPanelClose ? 0 : PANEL_WIDTH)
  const isAnimating = useRef(false)
  const [chatHandle, setChatHandle] = useState<ChatHandle | null>(null)
  const [baseChatHandle, setBaseChatHandle] = useState<BaseChatHandle | null>(null)
  const user = useSelector(getAuthUser)
  const [savedTemplateId, setSavedTemplateId] = useState<string | null>(null)
  const { markMessagesUnread, canUpdateReadState } = useMarkMessagesUnread()
  const { formatMessage } = useIntl()
  const isProviderPairReadOnly = providerPairAccess === ProviderPairAccess.READ
  const providerPairPresentation = getProviderPairPresentation(providerPairAccess, providerPairStatus)

  const composerNotice = useMemo(
    () => (showProviderPairResponseTimeNotice ? <ProviderPairResponseTimeNotice /> : undefined),
    [showProviderPairResponseTimeNotice],
  )
  useEffect(() => {
    const newValue = shouldPanelClose ? 0 : PANEL_WIDTH
    if (isAnimating.current) {
      return
    }
    // Only animate when panel is opened via user interaction
    if (prevPanelState == null || prevPanelState === isDetailsPanelOpen) {
      contentMarginRight.value = newValue
      return
    }

    isAnimating.current = true
    contentMarginRight.value = withTiming(newValue, {}, () => {
      isAnimating.current = false
    })
  }, [contentMarginRight, isDetailsPanelOpen, prevPanelState, shouldPanelClose])

  const contentStyle = useAnimatedStyle(() => ({
    marginRight: contentMarginRight.value,
  }))

  useEffect(() => {
    setSavedTemplateId(currentTemplateId ?? null)
  }, [currentTemplateId])

  useEffect(() => {
    // Refocus once loading settles; focus applied while a client is being selected can be lost.
    if (clientName && !isLoading && canSendMessages) {
      chatHandle?.focusInput()
    }
  }, [canSendMessages, chatHandle, clientName, isComposing, isLoading])

  useEffect(() => {
    if (!messageToInsert || !baseChatHandle) {
      return
    }
    if (isProviderPairReadOnly) {
      setSavedTemplateId(null)
      onMessageInserted()
      onReadOnlyInsertAttempt()
      return
    }
    if (!baseChatHandle.isReady) {
      return
    }
    baseChatHandle.insertMessage(messageToInsert.message, messageToInsert.opts)
    onMessageInserted()
  }, [
    setChatHandle,
    messageToInsert,
    onMessageInserted,
    onReadOnlyInsertAttempt,
    baseChatHandle,
    isProviderPairReadOnly,
    currentTemplateId,
  ])

  useEffect(() => {
    if (!shouldInsertDBL || !baseChatHandle) {
      return
    }
    if (isProviderPairReadOnly) {
      onDBLInserted()
      onReadOnlyInsertAttempt()
      return
    }
    if (!baseChatHandle.isReady) {
      return
    }
    baseChatHandle.insertDirectBookingLink()
    onDBLInserted()
  }, [shouldInsertDBL, onDBLInserted, onReadOnlyInsertAttempt, baseChatHandle, isProviderPairReadOnly])

  useEffect(() => {
    if (!shouldInsertEssentialsLink || !baseChatHandle) {
      return
    }
    if (isProviderPairReadOnly) {
      onEssentialsLinkInserted()
      onReadOnlyInsertAttempt()
      return
    }
    if (!baseChatHandle.isReady) {
      return
    }
    baseChatHandle.insertEssentialsLinkUrl()
    onEssentialsLinkInserted()
  }, [
    shouldInsertEssentialsLink,
    onEssentialsLinkInserted,
    onReadOnlyInsertAttempt,
    baseChatHandle,
    isProviderPairReadOnly,
  ])

  useEffect(() => {
    if (!documentToAttach || !baseChatHandle) {
      return
    }
    if (isProviderPairReadOnly) {
      onDocumentAttached()
      onReadOnlyInsertAttempt()
      return
    }
    if (!baseChatHandle.isReady) {
      return
    }
    baseChatHandle.attachDocument(documentToAttach)
    onDocumentAttached()
  }, [documentToAttach, onDocumentAttached, onReadOnlyInsertAttempt, baseChatHandle, isProviderPairReadOnly])

  return (
    <Container testID={tID('MessengerChat')} style={contentStyle}>
      {compactHeader ? (
        <CompactMessengerHeader
          {...compactHeader}
          clientName={clientName}
          onExpand={
            compactHeader.onExpand
              ? () => compactHeader.onExpand?.(baseChatHandle?.takeMessageForTransfer() ?? '')
              : undefined
          }
          headerNote={
            providerPairPresentation.headerNote ? formatMessage(providerPairPresentation.headerNote) : undefined
          }
        />
      ) : (
        <ProviderChatHeader
          clientName={clientName}
          title={title}
          titleAccessory={
            providerPairStatus ? (
              <ProviderPairStatusBadge status={providerPairStatus} testID={tID('ProviderPairStatusBadge-title')} />
            ) : undefined
          }
          isComposing={isComposing}
          onOpenDetailsPanel={onOpenDetailsPanel}
          isDetailsPanelOpen={isDetailsPanelOpen}
          clients={clients}
          onClientSelected={onClientSelected}
          onOpenNewWindow={onOpenNewWindow}
          onClose={onClose}
          onOpenMessages={
            onOpenMessages ? () => onOpenMessages(baseChatHandle?.takeMessageForTransfer() ?? '') : undefined
          }
          tabs={tabs}
          selectedTabId={selectedTabId}
          onTabSelected={onTabSelected}
          handleClientHeadingPressed={isClientTitle || isComposing ? onOpenClientProfile : undefined}
          markMessagesUnread={
            canUpdateReadState && hasRecipientMessages && canSendMessages ? markMessagesUnread : undefined
          }
          patientFullName={patientFullName}
          guardianName={guardianName}
          headerNote={
            providerPairPresentation.headerNote ? formatMessage(providerPairPresentation.headerNote) : undefined
          }
          isTitleMuted={Boolean(providerPairPresentation.headerNote)}
        />
      )}
      {isVerifyingSelectedThread ? (
        <VerificationContainer testID={tID('ProviderPair-verifying')}>
          <LoadingIndicator size={45} />
        </VerificationContainer>
      ) : showEmptyView ? (
        <MessengerEmptyView user={user} />
      ) : (
        <BaseChat
          ref={setBaseChatHandle}
          closePopover={onClose}
          selectedEpisodeId={selectedEpisodeId}
          hideChatHeader
          expandedButton
          showEmptyChatView={showEmptyComposeView}
          disableSend={showEmptyComposeView}
          ensureConversation={ensureConversation}
          receiverMsgable={canSendMessages}
          isProviderPairReadOnly={isProviderPairReadOnly}
          isProviderPair={providerPairAccess !== undefined}
          readOnlyFooter={
            providerPairPresentation.readOnlyFooter ? formatMessage(providerPairPresentation.readOnlyFooter) : undefined
          }
          composerNotice={composerNotice}
          showInputActions={showInputActions}
          disableDrafts={disableDrafts}
          recipientName={recipientName}
          recipientId={recipientId}
          isLoading={isLoading && !isComposing}
          onMessageSent={onMessageSent}
          entryPoint={entryPoint}
          onChatHandleChange={setChatHandle}
          savedTemplateId={savedTemplateId ?? undefined}
          setSavedTemplateId={setSavedTemplateId}
          onOpenDocumentLibrary={onOpenDocumentLibrary}
          onOpenSideBarItem={onOpenSideBarItem}
        />
      )}
    </Container>
  )
}

export default MessengerChat
